import { useEffect, useRef, useState, type ReactNode } from 'react';
import { remainingLabel } from '../../lib/cra';
import { shortUtc } from '../../lib/contract';
import { Btn } from './primitives';

// ── Live countdown. Anchors to a deadline (ms) computed from T0 (REVIEW-FIXES C-1);
//    always shows the T0 "aware since" line (REVIEW-FIXES U-3). ──
export function Countdown({ deadlineMs, cap, capWaiting, t0Iso, big }:
  { deadlineMs: number | null; cap: string; capWaiting?: string; t0Iso: string; big?: boolean }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const r = remainingLabel(deadlineMs, now);
  const color = r.overdue ? 'var(--critical)' : undefined;
  return (
    <div className="clock">
      {r.waiting
        ? <div className="cap" style={{ maxWidth: 220 }}>{capWaiting ?? cap}</div>
        : <>
            <div className="time mono" style={{ color, fontSize: big ? undefined : 'calc(30px*var(--fs))' }}>{r.text}</div>
            <div className="cap">{cap}</div>
          </>}
      <div className="cap mono" style={{ opacity: 0.85 }}>aware since {shortUtc(t0Iso)}</div>
    </div>
  );
}

// ── Deliberate-friction confirm panel (open → consequence → acknowledge → confirm) ──
export function FrictionPanel({ title, consequence, tone = 'medium', ackLabel, confirmLabel, onConfirm, onCancel }:
  {
    title: ReactNode; consequence: ReactNode; tone?: 'medium' | 'critical' | 'safe' | 'teal';
    ackLabel: ReactNode; confirmLabel: string; onConfirm: () => void; onCancel: () => void;
  }) {
  const [ack, setAck] = useState(false);
  const bg = `color-mix(in srgb, var(--${tone === 'medium' ? 'medium' : tone}) 9%, transparent)`;
  return (
    <div className="friction">
      <div className="fric-title">{title}</div>
      <div className="vex-conseq" style={{ marginTop: 10, background: bg }}>{consequence}</div>
      <label className="ck" style={{ marginTop: 12, alignItems: 'flex-start', gap: 10 }}>
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} style={{ marginTop: 2 }} />
        <span>{ackLabel}</span>
      </label>
      <div className="toolbar" style={{ marginTop: 14 }}>
        <Btn variant="primary" disabled={!ack} onClick={onConfirm}>{confirmLabel}</Btn>
        <Btn onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
}

// ── Async task state (spinner-ish placeholder) ──
export function Loading({ label = 'Loading…' }: { label?: string }) {
  return <div className="hint" style={{ padding: 24, textAlign: 'center' }}>{label}</div>;
}

export function useToast(): [ReactNode, (msg: string) => void] {
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const show = (m: string) => {
    setMsg(m);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMsg(null), 3200);
  };
  const node = msg ? (
    <div style={{ position: 'fixed', bottom: 22, left: '50%', transform: 'translateX(-50%)', zIndex: 90, background: 'var(--ink)', color: 'var(--page-bg)', padding: '10px 16px', borderRadius: 8, fontSize: 'calc(13px*var(--fs))', boxShadow: 'var(--shadow-lift)' }}>{msg}</div>
  ) : null;
  return [node, show];
}
